import React from "react";
import Modal from "../../../../components/common/Modal";
import ContentToggle from "../../../../components/common/ContentToggle";

interface ProjectHelpModalProps {
  isOpen: boolean;
  onClose: () => void;
}

/**
 * ProjectHelpModal - Modal help dialog for the "Project Details" feature.
 *
 * Displays frequently asked questions about the Project Details tab using
 * expandable ContentToggle sections. Opened from the Project Details page
 * help popup when the user clicks "additional page help".
 */
const ProjectHelpModal: React.FC<ProjectHelpModalProps> = ({
  isOpen,
  onClose,
}) => {
  const MODAL_STYLES = `
  #modal-manual-trig .modal-container {
    max-width: 100% !important;
    width: 100% !important;
    top: 10% !important;
  }
`;
  return (
    <Modal
      isOpen={isOpen}
      title="Project Questions & Answers"
      onClose={onClose}
      onSecondary={onClose}
      clearBtn="Close"
      contentClassName="mb-4!"
    >
      <div className="py-2">
        <ContentToggle
          titleText="What's new in Project Details tab?"
          isFillOption
        >
          <p className="text-[14px]!">
            Fields in the Project Detail Tab are organized by purpose. Each
            section has a question title and related fields.
          </p>
          <ol className="list-decimal list-inside mt-2 space-y-1">
            <li>What are we trying to accomplish?</li>
            <li>How will we know change is an improvement?</li>
            <li>What changes can we make that result in improvement?</li>
          </ol>
          <p className="mt-3! text-[14px]!">
            UBT Tracker allows you to collapse or expand individual sections by
            clicking the minus or plus sign located on the right side of each
            section title (blue row). You can also collapse or expand{" "}
            <strong>all</strong> sections by selecting the Close/Expand All
            Panes Button located on the right side of the Project Details Tab.
            This is a useful feature that can be used to better visualize data
            entered into the system.
          </p>
          <p className="mt-3! text-[14px]!">
            Teams can now choose from a wider range of tools that they may be
            using on the project (e.g. RIM, Process Mapping, and Driver
            Diagrams). Collecting this information will help Improvement
            Advisors, UBT Consultants, and other trainers to identify which
            tools are of greatest use to teams.
          </p>
          <p className="mt-3! text-[14px]!">
            Teams can now set specific numerical goals for their metrics,
            entering a baseline, threshold, target and/or stretch goals.
          </p>
          <p className="mt-3! text-[14px]!">
            Teams can attach documents or information to any project by entering
            a URL to a web-based location such as Ideabook or Sharepoint.
            Co-Leads/Proxy need to enter the complete information for the URL
            such as{" "}
            <a
              href="https://www.lmpartnership.org"
              target="_blank"
              rel="noopener noreferrer"
              className="text-kp-blue-dark! underline! hover:text-kp-blue-dark!"
            >
              https://www.lmpartnership.org
            </a>
            , or{" "}
            <a
              href="https://ideabook.kp.org/groups/ubt-tracker-implementation"
              target="_blank"
              rel="noopener noreferrer"
              className="text-kp-blue-dark! underline! hover:text-kp-blue-dark!"
            >
              https://ideabook.kp.org/groups/ubt-tracker-implementation
            </a>{" "}
            for the Ideabook group ubt-tracker-implementation.
          </p>
        </ContentToggle>

        <ContentToggle
          titleText="What are we trying to accomplish?"
          isFillOption
        >
          <p className="text-[14px]!">
            Give a short description or name to your project, set the start
            date, focus area and define your SMART goal.
          </p>
          <ul className="list-disc list-inside mt-2 space-y-1">
            <li>What are we trying to accomplish?</li>
            <li>How will we know change is an improvement?</li>
            <li>What changes can we make that result in improvement?</li>
          </ul>
          <p className="mt-3! text-[14px]!">
            The Project Details Tab is designed to be a planning tool for teams
            and a tool to share information with sponsors or other teams working
            on similar projects. UBTs that fill out the Project Details
            Tab&mdash;and make use of its companion Test of Change
            tab&mdash;will have essentially completed the first step of the{" "}
            <strong>P</strong>lan <strong>D</strong>o <strong>S</strong>tudy{" "}
            <strong>A</strong>ct Cycle (PDSA). The Project Details Tab and the
            Test of Change tab work together to be a record of the team&rsquo;s
            successes and challenges so they can learn from both.
          </p>
        </ContentToggle>

        <ContentToggle
          titleText='How do I fill out the "what are we trying to accomplish?" section?'
          isFillOption
        >
          <p className="text-[14px]!">
            Give a short description or name to your project, set the start
            date, focus area and define your SMART goal.
          </p>
          <p className="mt-3! text-[14px]!">
            This is the section where you give your project a name, enter its
            starting date and choose what Focus Area your project falls into,
            picking from the list defined by your region. However, the most
            important element is having a clearly stated SMART goal with a
            specific measure, a goal, and a deadline. See the context sensitive
            help for this field or talk to your UBT Consultant, Improvement
            Advisor (IA), or Sponsor for more information.
          </p>
          <p className="mt-3! text-[14px]!">
            SMART Goal Example: To improve mammogram screening of eligible (ages
            50&ndash;70) adult medicine patients from 50% in Jan 2012 to 90% in
            Dec 2012.
          </p>
        </ContentToggle>

        <ContentToggle
          titleText='How do I fill out the "How will we know that change is an improvement?" section?'
          isFillOption
        >
          <p className="text-[14px]!">
            This section focuses on measurement. What measure will you use to
            see if you are getting better? You can choose from a list of metrics
            defined by your region and set targets using the Baseline,
            Threshold, Target and Stretch fields. If your region has a web site
            with data on that metric, a link will appear in the field labeled
            &ldquo;Link to Metric Information.&rdquo; Roll your mouse arrow over
            the help icons next to each field for more information.
          </p>
          <p className="mt-3! text-[14px]!">
            The focus area choice fills in the performance metric list. If you
            don&rsquo;t see your regionally defined metric in the metric
            dropdown list perhaps it&rsquo;s under a different focus area. If
            the team is working on a metric not defined by the region select the
            metric &ldquo;other&rdquo; and enter the metric name in the
            description data box. If selecting &ldquo;other&rdquo; in the data
            source for metric dropdown, enter the tool or report the team is
            using to track the metric value.
          </p>
          <p className="mt-3! text-[14px]!">
            Communicate the team goal for the project by filling in the goal
            setting fields of baseline, threshold, target, and stretch:
          </p>
          <ul className="list-disc list-inside mt-2 space-y-1">
            <li>
              <strong>Baseline</strong> &ndash; Established criteria or original
              value before you execute test of changes
            </li>
            <li>
              <strong>Threshold</strong> &ndash; Minimum accepted criteria or
              value for project improvement
            </li>
            <li>
              <strong>Target</strong> &ndash; Desired goal or criteria for
              project success
            </li>
            <li>
              <strong>Stretch</strong> &ndash; Value or criteria above target.
              Incentive or challenge to the UBT
            </li>
          </ul>
          <p className="mt-3! text-[14px]!">
            If the region has identified a website for the published report for
            the metric, a link is displayed in the next blue section under link
            to metric report. Click the link to go to the report and look up the
            value to use in the baseline field.
          </p>
        </ContentToggle>

        <ContentToggle
          titleText='How do I fill out the "what changes can we make that will result in improvement?" section?'
          isFillOption
        >
          <p className="text-[14px]!">
            This section is about your ideas for improvement. What will make
            things better? If you need some ideas, click on the &ldquo;Ideas
            from UBTs doing similar work&rdquo; web site link, which, based on
            your choice of metric, automatically searches the national LMP
            website for information on teams doing similar projects. If defined
            by your region, clicking on the &ldquo;Tools and Tips&rdquo; data
            charts link will take you to a website that contains tools and tips
            specific to that metric. Record the various tools you are using in
            your project, like RIM or process maps. Roll your mouse arrow over
            the help icons next to each field for more information.
          </p>
          <p className="mt-3! text-[14px]!">
            Research articles and other similar projects from other UBTs, log
            tools used in this process of improvement, visit regional links with
            more tools &amp; tips related to the project.
          </p>
        </ContentToggle>

        <ContentToggle
          titleText='How do I fill out the "what results did we obtain?" section?'
          isFillOption
        >
          <p className="text-[14px]!">
            Log results and lessons learned. Summarize the whole project in a
            few sentences. UBT users should have an overview of what worked and
            didn&rsquo;t work by reading this section of your project detail.
          </p>
          <p className="mt-3! text-[14px]!">
            <strong>Results Summary and Results Description:</strong> This is
            where you record&mdash;briefly!&mdash;the results you are getting
            from your work. There is a drop down menu where you can update your
            project status (e.g. &ldquo;Target Achieved,&rdquo; &ldquo;Making
            Progress,&rdquo; etc.). In the results field, describe your current
            performance. Don&rsquo;t feel you need to write a novel or describe
            everything you did. Just focus on the results, particularly where
            you are on the measures.
          </p>
          <p className="mt-2! text-[14px]!">
            For example, the admitting data accuracy metric describe results
            might be:
          </p>
          <ul className="list-disc list-inside mt-2 space-y-1">
            <li>1/12 65% Baseline</li>
            <li>3/12 71.2%</li>
            <li>
              6/12 78% Revenue capture increased 50% with WPS = 0 (no repetitive
              injuries)
            </li>
          </ul>
          <p className="mt-3! text-[14px]!">
            <strong>Learnings or successful practices:</strong> Share the
            team&rsquo;s key learnings from the project here. Again, no need to
            write a novel. A single sentence that begins with &ldquo;The most
            successful thing we tested was&hellip;&rdquo; or &ldquo;The major
            obstacle we had to overcome was&hellip;&rdquo; may well be
            sufficient. You don&rsquo;t have to summarize each test of change
            &mdash; that is what the test of change tab is for. For example, the
            admitting data accuracy metric learnings might be:
          </p>
          <ul className="list-disc list-inside mt-2 space-y-1">
            <li>
              3/12 By using a checking list to confirm each data point is
              collected results have improved. Check list is available in our
              project document link.
            </li>
            <li>4/12 Training each employee is key to success.</li>
          </ul>
          <p className="mt-3! text-[14px]!">
            Roll your mouse arrow over the help icons next to each field for
            more information.
          </p>
        </ContentToggle>

        <ContentToggle
          titleText='How do I fill out the "define and track your own outcome measure" section?'
          isFillOption
        >
          <p className="text-[14px]!">
            Name the dimensions or variables in order to track performance and
            project outcome. The fields in this section should be text; use a
            word or a phrase to describe the data being collected.{" "}
            <strong className="underline">
              Do not use numeric values for these fields.
            </strong>
          </p>
          <p className="mt-3! text-[14px]!">
            For example, the improve mammography screen metric might define:
          </p>
          <ul className="list-disc list-inside mt-2 space-y-1">
            <li>Numerator Label: # Getting Mammography</li>
            <li>Denominator Label: # Needing Mammography</li>
            <li>Calculated Value Label: Mammography Rate</li>
          </ul>
          <p className="mt-3! text-[14px]!">
            Sometimes, the metric that you are using to track success
            isn&rsquo;t quite right or doesn&rsquo;t come out often enough. You
            may want to identify a &ldquo;driver&rdquo; of that metric (i.e.
            something that affects it) that you can collect data on yourself.
            Once you define a metric and enter a Test of Change, you can start
            entering data on that metric on the Test Data tab and even create
            simple run charts. The{" "}
            <a
              href="https://www.lmpartnership.org/tools/ubt-tracker-user-guide"
              target="_blank"
              rel="noopener noreferrer"
              className="text-kp-blue-dark! underline! hover:text-kp-blue-dark!"
            >
              UBT Tracker Manual
            </a>{" "}
            has more detail on how to do this, although you can also obtain help
            by rolling your mouse over the help icons on each field.
          </p>
        </ContentToggle>

        <ContentToggle
          titleText="What is the project information documents section for?"
          isFillOption
        >
          <p className="text-[14px]!">
            Some teams store their meeting notes, storybooks or other documents
            on a Sharepoint or Ideabook site. You can create a link to that site
            by inserting the URL here. This is an easy way to share project
            information with sponsors and other teams.
          </p>
          <p className="mt-3! text-[14px]!">
            Co-leads or the team proxy need to enter the complete information
            for the URL.
          </p>
          <p className="mt-3! text-[14px]!">
            For example:{" "}
            <a
              href="https://ideabook.kp.org/groups/ubt-tracker-implementation"
              target="_blank"
              rel="noopener noreferrer"
              className="text-kp-blue-dark! underline! hover:text-kp-blue-dark!"
            >
              https://ideabook.kp.org/groups/ubt-tracker-implementation
            </a>
          </p>
          <p className="mt-3! text-[14px]!">
            Roll your mouse arrow over the help icons next to the field for more
            information.
          </p>
          <p className="mt-4! font-semibold!">See Also</p>
          <ul className="mt-2! space-y-1!">
            <li>
              <a
                href="https://www.lmpartnership.org/ubt/rapid-improvement-model"
                target="_blank"
                rel="noopener noreferrer"
                className="text-kp-blue-dark! underline! hover:text-kp-blue-dark!"
              >
                The Rapid Improvement Model (RIM)
              </a>
            </li>
            <li>
              <a
                href="https://www.lmpartnership.org/tools/rapid-improvement-model-rim-pdsa"
                target="_blank"
                rel="noopener noreferrer"
                className="text-kp-blue-dark! underline! hover:text-kp-blue-dark!"
              >
                Plan, Do, Study, Act (PDSA) Cycle
              </a>
            </li>
            <li>
              <a
                href="https://www.lmpartnership.org/tools/ubt-tracker-user-guide"
                target="_blank"
                rel="noopener noreferrer"
                className="text-kp-blue-dark! underline! hover:text-kp-blue-dark!"
              >
                https://www.lmpartnership.org/tools/ubt-tracker-user-guide
              </a>
            </li>
          </ul>
        </ContentToggle>
      </div>
      <style>{MODAL_STYLES}</style>
    </Modal>
  );
};

export default ProjectHelpModal;
